/**
 * The plain-form fallback: the only non-chat way in, and when it appears.
 *
 * The chat is the front door — no buttons beside it. The links to the plain
 * forms appear only when the assistant cannot take the conversation: the
 * capabilities endpoint says it is not running, or a turn has just failed with
 * `ASSISTANT_UNAVAILABLE`. Then they sit inline in that notice or error, so an
 * employee is never stuck.
 *
 * Pure data and one predicate, no React — so `node --test` can import it
 * directly.
 */

/** The error code that brings the fallback links out. */
export const ASSISTANT_UNAVAILABLE_CODE = "ASSISTANT_UNAVAILABLE";

export type AssistantFallbackKey = "reportProblem" | "requestSomething" | "myTickets";

export interface AssistantFallbackLink {
  /** i18n key under `assistant:fallback.<key>`. */
  key: AssistantFallbackKey;
  path: string;
}

/** The three plain forms, in the order they are offered. */
export const ASSISTANT_FALLBACK_LINKS: readonly AssistantFallbackLink[] = [
  { key: "reportProblem", path: "/tickets/new" },
  { key: "requestSomething", path: "/tickets/new?kind=SERVICE_REQUEST" },
  { key: "myTickets", path: "/tickets" },
];

export interface FallbackInput {
  /** `/meta/capabilities` → `assistant.available`; `null` while unknown. */
  available: boolean | null;
  /** The error code of the notice or failed turn being rendered, if any. */
  errorCode?: string | null;
}

/**
 * Whether to show the plain-form links.
 *
 * Only a definite `false` from capabilities counts, so nothing flashes while
 * capabilities load; any other failed turn gets "try again", not a way out of
 * the chat.
 */
export function showsFallbackLinks({ available, errorCode = null }: FallbackInput): boolean {
  return available === false || errorCode === ASSISTANT_UNAVAILABLE_CODE;
}
